import { z } from 'zod';
import { apiGet, apiGetParsed } from './client';
import { parseOrWarn } from '@/lib/zod-resolver';
import { paginationSchema } from './_types/common';
import type { Pagination } from './_types/common';

/**
 * Subject 多型抽象（v4 M3-α）。
 *
 * R3-Sprint7a Implementer Group A 切真:
 *   后端无 `/subjects` endpoint(grep backend_app/app/api/v1/ 确认);
 *   兜底走 `GET /enterprises`(只返 {id, name}),前端补足 Subject 形状。
 *
 * Sprint 16 改造:
 *   - 删 enterprise_type / incubation_status(BE 已删字段)
 *   - status 派生改用 graduated_date / incubation_entry_date 存在性
 *   - 透传 qualifications: list(BE 在 /enterprises list 端预取每企业 5 条 active)
 */

// Sprint 31-C — BE /subjects endpoint 已补全(查 subjects 表按 subject_type 过滤),
// 切到真接口让孵化器/园区/企业一起在「主体管理」展示
const _FETCH_VIA_ENTERPRISES = false;

export const subjectKindSchema = z.enum(['enterprise', 'incubator', 'university', 'team']);
export type SubjectKind = z.infer<typeof subjectKindSchema>;

/** 列表展示用的简化 qualification 子项(只要 code/name) */
const subjectQualificationSchema = z.object({
  code: z.string(),
  name: z.string(),
});

// Sprint 31-F:申报中 case(任何主体类型都可能在申报,孵化器自身亦可)
const subjectActiveCaseSchema = z.object({
  case_id: z.string(),
  status: z.string(),
  declare_year: z.number().int().nullable().optional(),
  policy_pack_id: z.string().nullable().optional(),
  qualification_code: z.string().nullable().optional(),
  qualification_name: z.string().nullable().optional(),
});
export type SubjectActiveCase = z.infer<typeof subjectActiveCaseSchema>;

export const subjectSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  kind: subjectKindSchema,
  status: z.enum(['active', 'inactive', 'graduated', 'archived']),
  // Sprint15-hotfix:后端 list_enterprises 返 tenant_slug + tenant_display_name
  // (batch lookup tenants 表注入)。老 tenant_code 字段保留兼容但已废弃。
  tenant_code: z.string().optional(),
  tenant_slug: z.string().nullable().optional(),
  tenant_display_name: z.string().nullable().optional(),
  tenant_id: z.string().uuid().nullable().optional(),
  credit_code: z.string().nullable().optional(),
  registered_date: z.string().nullable().optional(),
  // Sprint 16:已认定资质列表(BE list 端预取每企业 5 条 active catalog)
  qualifications: z.array(subjectQualificationSchema).default([]),
  // Sprint 31-F:申报中(非终止态)案 — 主体列表显示"在干什么"
  active_cases: z.array(subjectActiveCaseSchema).default([]),
  // Sprint58:园区归属 — 仅 enterprise 类型有意义(其他类型恒 null);TRUE=园区内可入孵
  in_park: z.boolean().nullable().optional(),
  // Sprint59:IP 简报(列表显示用;详情走 /ip-records/summary)
  ip_brief: z
    .object({
      total_active: z.number().int().nonnegative().default(0),
      last_year_invention: z.number().int().nonnegative().default(0),
    })
    .nullable()
    .optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  /** 简版指标，列表页用；详情留给后续 ρ-12 */
  metrics_brief: z
    .object({
      compliance_score: z.number().nullable().optional(),
      readiness_score: z.number().nullable().optional(),
    })
    .partial()
    .optional(),
});
export type Subject = z.infer<typeof subjectSchema>;

export const subjectListSchema = paginationSchema(subjectSchema);

export interface ListSubjectsParams {
  page?: number;
  page_size?: number;
  kind?: SubjectKind;
  status?: Subject['status'];
  q?: string;
  // Sprint58:园区归属过滤(true=园区内 false=园区外;不传=不限)
  in_park?: boolean;
}

/**
 * 后端 enterprise 行 → subject 形状(hand-off §3.1)
 *
 * Sprint 54:status 派生只看 graduated_date(incubation_entry_date 已删 — 走 enterprise_incubation 单一源)
 *   - 有 graduated_date → graduated
 *   - 否则              → inactive(实际 active/graduated 由 /subjects endpoint 真实计算)
 */
function _enterpriseToSubject(e: {
  id: string;
  name: string;
  tenant_id?: string | null;
  tenant_slug?: string | null;
  tenant_display_name?: string | null;
  credit_code?: string | null;
  registered_date?: string | null;
  graduated_date?: string | null;
  qualifications?: Array<{ catalog?: { code?: string; name?: string } | null } | null> | null;
}): Subject {
  let status: Subject['status'];
  if (e.graduated_date) {
    status = 'graduated';
  } else {
    status = 'inactive';
  }

  // 提 qualifications.catalog → {code, name},兼容 BE 嵌套结构
  const quals = (e.qualifications ?? [])
    .map((q) => q?.catalog)
    .filter((c): c is { code?: string; name?: string } => !!c)
    .map((c) => ({ code: String(c.code ?? ''), name: String(c.name ?? '') }))
    .filter((c) => c.code || c.name);

  return {
    id: e.id,
    name: e.name,
    kind: 'enterprise',
    status,
    tenant_id: e.tenant_id ?? null,
    tenant_slug: e.tenant_slug ?? null,
    tenant_display_name: e.tenant_display_name ?? null,
    credit_code: e.credit_code ?? null,
    registered_date: e.registered_date ?? null,
    qualifications: quals,
    active_cases: [],
  };
}

/**
 * 列表 — 优先真 `/subjects`，兜底 `/enterprises`。
 * 后端 `/enterprises` 当前不接 page / page_size 参数 — 前端做客户端分页 + 筛选。
 */
export async function listSubjects(
  params: ListSubjectsParams = {},
): Promise<Pagination<Subject>> {
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? 20;

  if (_FETCH_VIA_ENTERPRISES) {
    // /enterprises 兜底返裸 {items, total};Sprint 16 BE list 端会预取
    // qualifications/credit_code/...,这里宽松接 unknown 再 _enterpriseToSubject 兜底。
    let raw: { items: Array<Record<string, unknown>>; total: number };
    try {
      raw = await apiGetParsed(
        '/enterprises',
        z.object({
          items: z.array(z.record(z.unknown())),
          total: z.number().int().nonnegative(),
        }),
      );
    } catch {
      raw = { items: [], total: 0 };
    }
    let items: Subject[] = raw.items.map((row) =>
      _enterpriseToSubject(row as Parameters<typeof _enterpriseToSubject>[0]),
    );

    if (params.kind) items = items.filter((s) => s.kind === params.kind);
    if (params.status) items = items.filter((s) => s.status === params.status);
    if (params.q) {
      const q = params.q.toLowerCase();
      items = items.filter((s) => s.name.toLowerCase().includes(q));
    }
    const total = items.length;
    const start = (page - 1) * pageSize;
    const pageItems = items.slice(start, start + pageSize);
    return parseOrWarn(
      subjectListSchema as z.ZodType<Pagination<Subject>>,
      { items: pageItems, total, page, page_size: pageSize } as unknown,
      'GET /enterprises (mapped → subjects)',
    );
  }

  // 后端真出 /subjects 后走这条 — v4-R3-Sprint8 Group E:升级 apiGetParsed
  return apiGetParsed<Pagination<Subject>>(
    '/subjects',
    subjectListSchema as z.ZodType<Pagination<Subject>>,
    {
      ...params,
      page,
      page_size: pageSize,
    },
  );
}

export async function getSubject(id: number | string): Promise<Subject> {
  if (_FETCH_VIA_ENTERPRISES) {
    const safeId = encodeURIComponent(String(id));
    const raw = await apiGet<Record<string, unknown>>(`/enterprises/${safeId}`);
    return parseOrWarn(
      subjectSchema as z.ZodType<Subject>,
      _enterpriseToSubject(raw as Parameters<typeof _enterpriseToSubject>[0]) as unknown,
      `GET /enterprises/${id}`,
    );
  }
  const safeId = encodeURIComponent(String(id));
  const raw = await apiGet(`/subjects/${safeId}`);
  return parseOrWarn(subjectSchema as z.ZodType<Subject>, raw, `GET /subjects/${id}`);
}
